import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Broadcast, ArrowLeft } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PropCard } from "../components/PropCard";
import { getUserToken, toggleWatchlist, fetchWatchlist } from "../lib/api";

export default function LiveNow() {
  const [props, setProps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [watched, setWatched] = useState(new Set());
  const { user } = useAuth();
  const token = getUserToken();

  const load = async () => {
    try {
      const { data } = await api.get("/props/live-now");
      setProps(data.props || []);
    } catch (e) {
      console.debug("live-now fetch failed", e);
      setProps([]);
    } finally {
      setLoading(false);
    }
    try {
      const w = await fetchWatchlist(token);
      setWatched(new Set((w.props || []).map((p) => p.id)));
    } catch (e) {
      console.debug("watchlist fetch failed", e);
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleWatch = async (propId) => {
    try {
      const res = await toggleWatchlist(token, propId);
      setWatched((prev) => {
        const next = new Set(prev);
        if (res.status === "added") next.add(propId);
        else next.delete(propId);
        return next;
      });
    } catch (e) {
      console.debug("watchlist toggle failed", e);
    }
  };

  return (
    <div data-testid="live-now-page">
      {/* Hero */}
      <section className="border-b border-[#27272A]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
          <Link
            to="/"
            className="overline inline-flex items-center gap-1.5 hover:text-white transition-colors mb-4"
            data-testid="live-now-back"
          >
            <ArrowLeft size={12} weight="bold" />
            Back to Board
          </Link>
          <div className="flex items-center gap-3 mb-3">
            <span
              className="inline-flex items-center gap-1.5 px-2 py-1"
              style={{ background: "rgba(239,68,68,0.15)", color: "#ef4444" }}
            >
              <span
                className="w-2 h-2 rounded-full"
                style={{ background: "#ef4444", animation: "pulse 1.5s ease-in-out infinite" }}
              />
              <span className="overline" style={{ color: "#ef4444", letterSpacing: "0.14em" }}>LIVE NOW</span>
            </span>
            <span className="overline">Auto-refresh every 30s</span>
          </div>
          <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl text-white leading-tight tracking-tight">
            Picks in Progress
          </h1>
          <p className="text-[#A1A1AA] max-w-xl mt-4 text-sm sm:text-base">
            Track how your plays are doing right now. These picks have moved past the early-live cutoff so they no longer show on the main board — but the games are still running. Grading happens automatically once each game ends.
          </p>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        {loading ? (
          <div className="text-[#A1A1AA] text-sm py-20 text-center" data-testid="live-now-loading">
            Checking live games…
          </div>
        ) : props.length === 0 ? (
          <div className="py-20 text-center max-w-lg mx-auto" data-testid="live-now-empty">
            <Broadcast size={40} weight="thin" style={{ color: "#71717A", margin: "0 auto 12px" }} />
            <div className="overline mb-3 text-[#71717A]">Nothing Live Right Now</div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed">
              No picks are currently in progress. Check back after the next slate goes off — this page auto-refreshes every 30 seconds.
            </p>
            <Link
              to="/"
              className="inline-block mt-6 border border-[#27272A] px-4 py-2 overline hover:text-white hover:border-white/30 transition-colors"
              data-testid="live-now-empty-back"
            >
              Back to Board
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="live-now-grid">
            {props.map((p, i) => (
              <PropCard
                key={p.id}
                prop={p}
                index={i}
                onToggleWatch={handleWatch}
                isWatched={watched.has(p.id)}
                onCreatorPinChange={load}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
